/**
 * Application preferences — theme, density, animations, language, date and
 * number format. Every control here is wired to a real consumer:
 *  - theme/density/animations  → `PreferencesProvider` toggles `<html>` classes
 *  - language                  → i18next via `PreferencesProvider`
 *  - dateFormat/numberFormat   → `useFormat()` consumers (Dashboard etc.)
 *
 * Previously this section had accent-colour + font-family + timezone-list
 * controls that persisted to localStorage but were never read. They were
 * removed (along with the matching `usePersistentState` keys) so the UI
 * doesn't lie about what's wired up.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Icons } from '@/src/modules/components/Icons'
import { cn } from '@/src/infrastructure/lib/utils'
import {
  SettingsCard,
  SettingRow,
  ToggleSwitch,
  SelectField,
} from '@/src/modules/components/settings/primitives'
import { usePreferences } from '@/src/modules/preferences/PreferencesProvider'
import { useFormat } from '@/src/infrastructure/lib/format'
import type {
  DateFormat,
  Language,
  NumberFormat,
  ThemeMode,
} from '@/src/modules/preferences/preferences'

const DATE_OPTIONS: { value: DateFormat; label: string }[] = [
  { value: 'DD MMM YYYY', label: '25 Dec 2026' },
  { value: 'DD/MM/YYYY', label: '25/12/2026' },
  { value: 'MM/DD/YYYY', label: '12/25/2026' },
  { value: 'YYYY-MM-DD', label: '2026-12-25' },
]

const NUMBER_OPTIONS: { value: NumberFormat; label: string }[] = [
  { value: 'en-IN', label: '12,34,567.89 (Indian)' },
  { value: 'en-US', label: '1,234,567.89 (US/UK)' },
  { value: 'de-DE', label: '1.234.567,89 (European)' },
]

const LANGUAGE_OPTIONS: { value: Language; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिन्दी' },
]

export function AppPreferencesSection() {
  const { t } = useTranslation('settings')
  const { preferences, setPreference } = usePreferences()
  const fmt = useFormat()

  return (
    <div className="grid lg:grid-cols-[1fr_18rem] gap-6 items-start">
      <div className="flex flex-col gap-6">
        <SettingsCard
          icon={Icons.Palette}
          title={t('preferences.appearanceTitle')}
          description={t('preferences.appearanceDescription')}
        >
          <SettingRow
            label={t('preferences.theme')}
            description={t('preferences.themeDescription')}
          >
            <div className="flex gap-2">
              {(['light', 'dark', 'system'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setPreference('theme', t as ThemeMode)}
                  className={cn(
                    'flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold capitalize transition-all',
                    preferences.theme === t
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-surface-low text-on-surface-variant hover:bg-outline-variant/20',
                  )}
                >
                  {t === 'light' && <Icons.Sun size={15} />}
                  {t === 'dark' && <Icons.Moon size={15} />}
                  {t === 'system' && <Icons.Settings size={15} />}
                  {t}
                </button>
              ))}
            </div>
          </SettingRow>

          <SettingRow
            label={t('preferences.density')}
            description={t('preferences.densityDescription')}
          >
            <div className="flex gap-2">
              {(['comfortable', 'compact'] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setPreference('density', d)}
                  className={cn(
                    'rounded-xl px-3.5 py-2 text-sm font-semibold capitalize transition-all',
                    preferences.density === d
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-surface-low text-on-surface-variant hover:bg-outline-variant/20',
                  )}
                >
                  {d}
                </button>
              ))}
            </div>
          </SettingRow>

          <SettingRow
            label={t('preferences.animations')}
            description={t('preferences.animationsDescription')}
            htmlFor="pref-anim"
          >
            <ToggleSwitch
              id="pref-anim"
              checked={preferences.animationsEnabled}
              onChange={(v) => setPreference('animationsEnabled', v)}
            />
          </SettingRow>
        </SettingsCard>

        <SettingsCard
          icon={Icons.Globe}
          title={t('preferences.regionTitle')}
          description={t('preferences.regionDescription')}
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <SelectField
              id="pref-lang"
              label={t('preferences.language')}
              options={LANGUAGE_OPTIONS}
              value={preferences.language}
              onChange={(e: any) =>
                setPreference('language', e.target.value as Language)
              }
            />
            <SelectField
              id="pref-date"
              label={t('preferences.dateFormat')}
              options={DATE_OPTIONS}
              value={preferences.dateFormat}
              onChange={(e: any) =>
                setPreference('dateFormat', e.target.value as DateFormat)
              }
            />
            <SelectField
              id="pref-num"
              label={t('preferences.numberFormat')}
              options={NUMBER_OPTIONS}
              value={preferences.numberFormat}
              onChange={(e: any) =>
                setPreference('numberFormat', e.target.value as NumberFormat)
              }
            />
          </div>
        </SettingsCard>
      </div>

      {/* Live preview — reflects the actual DOM-applied state, not a mock. */}
      <motion.aside
        layout
        className="sticky top-24 rounded-[1.75rem] border border-outline-variant/30 p-5 shadow-sm overflow-hidden bg-surface-lowest text-on-surface"
      >
        <p className="text-[11px] font-bold uppercase tracking-wide opacity-60 mb-3">
          {t('preferences.preview')}
        </p>
        <div className="rounded-2xl p-4 bg-surface-low">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white bg-primary">
              <Icons.Sparkles size={16} />
            </span>
            <div>
              <p className="text-sm font-bold leading-tight">Summer Wedding</p>
              <p className="text-xs opacity-60">{fmt.date(new Date())}</p>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            <div className="h-2 rounded-full bg-current opacity-15" />
            <div className="h-2 w-2/3 rounded-full bg-current opacity-15" />
          </div>
          <p className="text-xs opacity-60 mt-3">
            RSVPs: {fmt.number(1234567)}
          </p>
        </div>
        <p className="text-xs opacity-60 mt-3">
          {preferences.animationsEnabled ? 'Animations on' : 'Animations off'} ·{' '}
          {preferences.density} · {preferences.theme}
        </p>
      </motion.aside>
    </div>
  )
}
